<template>
    <div class="sf-video-question">
        <div v-if="fieldLabel" class="vq-label">
            {{ fieldLabel }}
            <span v-if="isRequired" class="vq-required">*</span>
        </div>

        <div class="vq-player-wrap">
            <iframe
                v-if="embedUrl"
                :src="embedUrl"
                class="vq-frame"
                frameborder="0"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                @load="onVideoLoad"
            ></iframe>
            <video
                v-else-if="assetSrc"
                :src="assetSrc"
                class="vq-video"
                controls
                preload="metadata"
                playsinline
            ></video>
            <div v-else class="vq-empty">Video yüklenemedi</div>
        </div>

        <div v-if="config.answerType && config.answerType !== 'none'" class="vq-answer">
            <input
                v-if="config.answerType === 'shortText'"
                type="text"
                class="vq-input"
                :value="(internalValue && internalValue.answer) || ''"
                :placeholder="answerPlaceholder"
                @input="emitAnswer($event.target.value)"
            />
            <textarea
                v-else-if="config.answerType === 'longText'"
                class="vq-textarea"
                :value="(internalValue && internalValue.answer) || ''"
                :placeholder="answerPlaceholder"
                rows="3"
                @input="emitAnswer($event.target.value)"
            />
            <div v-else-if="config.answerType === 'singleChoice'" class="vq-choice-list">
                <label
                    v-for="(opt, idx) in answerOptions"
                    :key="idx"
                    class="vq-choice-item"
                    :class="{ active: (internalValue && internalValue.answer) === opt }"
                >
                    <input
                        type="radio"
                        :checked="(internalValue && internalValue.answer) === opt"
                        :name="'vq-' + fieldId"
                        @change="emitAnswer(opt)"
                    />
                    <span>{{ opt }}</span>
                </label>
            </div>
            <div v-else-if="config.answerType === 'rating'" class="vq-rating">
                <span
                    v-for="i in 5"
                    :key="i"
                    class="vq-star"
                    :class="{ on: i <= ((internalValue && internalValue.answer) || 0) }"
                    @click="emitAnswer(i)"
                >★</span>
            </div>
        </div>

        <div v-if="showError && hasError" class="vq-error">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script>
export default {
    name: "VideoQuestion",
    props: {
        value: { type: [Object, String, Number, Array], default: null },
        videoQuestion: { type: Object, default: () => ({}) },
        label: { type: String, default: "" },
        required: { type: Boolean, default: false },
        showError: { type: Boolean, default: false },
        formId: { type: String, default: "" },
        fieldId: { type: String, default: "" },
        order: { type: Number, default: 0 },
    },
    data() {
        return {
            internalValue: this.value || { answer: null, watchedSeconds: 0 },
            watchedSeconds: 0,
        };
    },
    computed: {
        config() { return this.videoQuestion || {}; },
        fieldLabel() { return this.label || ""; },
        isRequired() { return !!this.required; },
        embedUrl() {
            const src = this.config.source;
            const url = this.config.externalUrl || "";
            if (src === "youtube") return this.toYoutubeEmbed(url);
            if (src === "vimeo") return this.toVimeoEmbed(url);
            return null;
        },
        assetSrc() {
            if (this.config.source !== "asset") return null;
            return this.resolveVideoUrl(this.config.externalUrl, this.config.asset);
        },
        answerOptions() {
            if (!this.config.answerConfig) return [];
            return this.config.answerConfig.items || this.config.answerConfig.options || [];
        },
        answerPlaceholder() {
            return (this.config.answerConfig && this.config.answerConfig.placeholder) || "Cevabınız...";
        },
        hasError() {
            if (!this.config.answerType || this.config.answerType === "none") return false;
            if (!this.isRequired) return false;
            const ans = this.internalValue && this.internalValue.answer;
            return ans === null || ans === undefined || ans === "" || (Array.isArray(ans) && ans.length === 0);
        },
        errorMessage() {
            return "Bu alan zorunludur";
        },
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal && typeof newVal === "object") {
                    this.internalValue = Object.assign({}, this.internalValue, newVal);
                }
            },
            immediate: true,
        },
    },
    methods: {
        resolveVideoUrl(externalUrl, asset) {
            if (externalUrl) return externalUrl;
            if (!asset) return null;
            if (typeof asset === "string") return asset;
            if (asset.cdnUrl) return asset.cdnUrl;
            if (asset.url) return asset.url;
            if (asset.path && asset._id && asset.ext) {
                return "https://static.supsis.live/" + asset.path + asset._id + "." + asset.ext;
            }
            return null;
        },
        toYoutubeEmbed(url) {
            if (!url) return null;
            var m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/);
            if (!m) return url;
            return "https://www.youtube.com/embed/" + m[1];
        },
        toVimeoEmbed(url) {
            if (!url) return null;
            var m = url.match(/vimeo\.com\/(\d+)/);
            if (!m) return url;
            return "https://player.vimeo.com/video/" + m[1];
        },
        onVideoLoad() { /* iframe load — placeholder */ },
        emitAnswer(answer) {
            this.$set(this.internalValue, "answer", answer);
            this.$emit("input", this.internalValue);
            this.$emit("validate", { fieldId: this.fieldId, hasError: this.hasError });
        },
    },
};
</script>

<style lang="scss" scoped>
.sf-video-question {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
}

.vq-label {
    font-weight: 600;
    color: var(--text-primary, #1f2937);
    font-size: clamp(14px, 1.8vw, 16px);
    .vq-required { color: #dc2626; margin-left: 4px; }
}

.vq-player-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000000;
    border-radius: 8px;
    overflow: hidden;

    .vq-frame,
    .vq-video {
        width: 100%;
        height: 100%;
        border: none;
        display: block;
    }
    .vq-empty {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #9ca3af;
        background: #f3f4f6;
    }
}

.vq-answer {
    margin-top: 4px;
}

.vq-input,
.vq-textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--border, #d1d5db);
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    background: #ffffff;
    &:focus { outline: none; border-color: var(--primary, #6366f1); }
}

.vq-choice-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.vq-choice-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid var(--border, #e5e7eb);
    border-radius: 8px;
    cursor: pointer;
    background: #ffffff;
    transition: border-color 0.15s, background-color 0.15s;

    &:hover { border-color: var(--primary, #6366f1); background: #fafbff; }
    &.active { border-color: var(--primary, #6366f1); background: var(--primary-bg, #eef2ff); }
}

.vq-rating {
    display: inline-flex;
    gap: 4px;

    .vq-star {
        font-size: 28px;
        line-height: 1;
        cursor: pointer;
        color: #d1d5db;
        transition: color 0.15s, transform 0.1s;
        user-select: none;

        &.on { color: #f59e0b; }
        &:hover { transform: scale(1.1); }
    }
}

.vq-error {
    color: #dc2626;
    font-size: 12px;
    margin-top: 4px;
}
</style>
